import { useEffect, useRef } from 'react';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { enhanceTreeNodePropsWithMore, useExpanded, getTreeNodeProps } from '@cld/tree';
import { selectTreeItems, selectTreeFolders, selectTreeStatuses, selectTreeDefaultExpanded } from '../../selectors/treeSelectors';
import bindActions, { types as ats } from '../../actions';
import { selectNavigationFolderId } from '../../selectors/navigationSelectors';
import { STATUSES } from '../../data/consts';
import Node from './Node';

const ROW_HEIGHT = 32;
const { LOAD_TREE_FOLDERS } = ats;
const getTreeNodeMoreProps = enhanceTreeNodePropsWithMore(getTreeNodeProps);

const TreeWrapper = styled.div`
  padding: 10px 0 0 0;
  width: 100%;
  height: 100%;
  flex-shrink: 0;
  overflow-x: hidden;

  > div > div {
    overflow: scroll !important;
  }
`;

const generateItemKey = (index, { nodes }) => `${nodes[index].nodeId}-tree`;

const Tree = ({ actions, defaultExpanded, treeData, treeFolders, treeStatuses, currentFolderId }) => {
  const expandedProps = useExpanded();
  const isFirstLoad = useRef(); // added to be able to add to effect dependency list

  useEffect(() => {
    if (defaultExpanded && !isFirstLoad.current) {
      expandedProps.setExpanded(defaultExpanded.id, true);
      actions[LOAD_TREE_FOLDERS](defaultExpanded);
      isFirstLoad.current = true;
    }
  }, [defaultExpanded, actions, isFirstLoad, expandedProps]);

  const nodes = treeData.reduce((acc, { id: nodeId, cursor, ...item }, idx) => {
    const results = getTreeNodeMoreProps({
      nodeId,
      showMore: cursor,
      cursor,
      isLast: idx === treeData.length - 1,
      ...item,
      ...expandedProps,
    });

    results.forEach((props) => {
      if (props.visible) {
        const { origNodeId, nodeId } = props;
        const stateProps = {
          selected: currentFolderId === nodeId,
          isAlreadyExpanded: treeStatuses[nodeId],
          progress: treeStatuses[origNodeId || nodeId] === STATUSES.PENDING,
        };

        acc.push({ ...props, ...stateProps });
      }
    });

    return acc;
  }, []);

  return (
    <TreeWrapper className="bg-window">
      <AutoSizer>
        {({ width, height }) => (
          <List
            height={height}
            width={width}
            itemSize={ROW_HEIGHT}
            itemCount={nodes.length}
            itemData={{
              nodes,
              expandedProps,
              actions,
              folders: treeFolders,
            }}
            itemKey={generateItemKey}
          >
            {Node}
          </List>
        )}
      </AutoSizer>
    </TreeWrapper>
  );
};

const mapStateToProps = (state) => ({
  treeData: selectTreeItems(state),
  treeFolders: selectTreeFolders(state),
  treeStatuses: selectTreeStatuses(state),
  currentFolderId: selectNavigationFolderId(state),
  defaultExpanded: selectTreeDefaultExpanded(state),
});

export default connect(mapStateToProps, (dispatch) => ({
  actions: bindActions(dispatch),
}))(Tree);
